import { useState, useCallback } from 'react';
import { checkValidators } from '../functions';
import { DefaultFormFieldProps, DefaultFormFieldReturnValue, ValidatorResult } from '../types';

export interface UseRichTextFormFieldReturnValue {
    handleChange: (value: string) => void;
    handleBlur: () => void;
}

export type ReturnValue<Value = string> = DefaultFormFieldReturnValue<Value> & UseRichTextFormFieldReturnValue;
export type Props<Value = string> = DefaultFormFieldProps<Value>;

function useRichTextFormField({ id, initialValue, validators }: Props): ReturnValue {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState<ValidatorResult>(null);

    const handleChange = useCallback(
        async (val: string) => {
            // Set new value
            setValue(val);

            // Check validity for a new value
            setError(await checkValidators(val, validators));
        },
        [validators]
    );

    const handleBlur = useCallback(async () => {
        // Check validity for a field
        setError(await checkValidators(value, validators));
    }, [value, validators]);

    const hasError = useCallback(async () => {
        // Check validity for a field
        const err = await checkValidators(value, validators);

        // Set validation result
        setError(err);

        return !!err;
    }, [value, validators]);

    const reset = () => {
        // Reset value
        setValue(initialValue);
    };

    return {
        id,
        value,
        error,
        reset,
        hasError,
        handleChange,
        handleBlur,
    };
}

export default useRichTextFormField;
